import { useCallback, useEffect, useMemo } from 'react';
import { useField, useFormikContext } from 'formik';
// Import Helpers
import { combineFieldNames } from './helpers';

interface UseDNDRowFormDataOptions {
    parentName: string;
    index: number;
}

export const useDNDCustomRowData = <R extends AnyObject>({ parentName, index }: UseDNDRowFormDataOptions) => {
    const rowName = combineFieldNames(parentName, index);

    const [, { value }] = useField<R>(rowName);
    const { errors: formErrors, touched: formTouched, handleChange, handleBlur } = useFormikContext<R>();

    useEffect(() => {
        if (index !== value.index) {
            handleChange({ target: { name: combineFieldNames(rowName, 'index'), value: index } });
        }
    }, [handleChange, index, rowName, value.index]);

    const onChange = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (name: string, value: any) => {
            handleChange({ target: { name, value } });
            handleBlur({ target: { name } });
        },
        [handleBlur, handleChange],
    );

    const onBlur = useCallback((name: string) => handleBlur({ target: { name } }), [handleBlur]);

    const getFieldName = useCallback((fieldName: string) => combineFieldNames(rowName, fieldName), [rowName]);

    const errors = useMemo(
        () => (formErrors?.[parentName]?.[index] as Record<keyof R, string | null>) ?? null,
        [formErrors, index, parentName],
    );

    const touched = useMemo(
        () => (formTouched?.[parentName]?.[index] as Record<keyof R, boolean>) ?? null,
        [formTouched, index, parentName],
    );

    return useMemo(
        () => ({
            values: (value as R) ?? {},
            errors,
            touched,
            onChange,
            onBlur,
            getFieldName,
        }),
        [errors, getFieldName, onBlur, onChange, touched, value],
    );
};
